import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Page, PageHeader, Card, Loading, usd, plural } from "@/components/common";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// design_handoff_crewbench/screens/Budgets.dc.html: every budget a request falls under, what each has used this period,
// and its limit. GET /budgets serves every scope the page offers, set or not, with its spend (`scopes`).
const month = (d = new Date()) => d.toLocaleDateString("en-GB", { month: "long" });
const nextMonth = () => { const d = new Date(); return `1 ${new Date(d.getFullYear(), d.getMonth() + 1, 1).toLocaleDateString("en-GB", { month: "long" })}`; };

// the bar: white, the warning colour from 80%, full in danger; 2px gaps at 50% and 80%, where alerts are sent
function BudgetBar({ p, tone }) {
  return (
    <div className="relative h-1.5 rounded-[3px] bg-gr-surface-2 overflow-hidden">
      <div className={cn("h-full", tone === "full" ? "bg-gr-danger" : tone === "near" ? "bg-gr-warning" : "bg-foreground")}
        style={{ width: `${tone === "full" ? 100 : Math.min(100, p)}%` }} />
      {[50, 80].map((m) => <span key={m} className="absolute inset-y-0 w-0.5 bg-card" style={{ left: `${m}%` }} aria-hidden="true" />)}
    </div>
  );
}

// a limit of $0 stops everything at once
const isFull = (r) => r.amount_usd != null && (r.amount_usd === 0 || r.spent_usd >= r.amount_usd);

function BudgetRow({ row, label, sub, aria, onSave, saving }) {
  const cap = row.amount_usd;
  const [draft, setDraft] = useState(null);
  const value = draft ?? (cap == null ? "" : String(cap));
  const dirty = draft !== null && draft !== "" && Number(draft) >= 0 && Number(draft) !== cap;
  const p = cap ? Math.round((row.spent_usd / cap) * 100) : 0;
  const tone = isFull(row) ? "full" : cap == null ? "none" : p >= 80 ? "near" : "ok";
  const used = cap == null ? `${usd(row.spent_usd)} spent · no limit set` : `${usd(row.spent_usd)} of ${usd(cap)} · ${Math.min(p, 999)}%`;
  const save = () => dirty && onSave(Number(draft), () => setDraft(null));
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 py-3.5 border-t" data-testid={`budget-row-${row.level}-${row.ref_id}`}>
      <div className="flex-[1_1_280px] min-w-0 flex flex-col gap-2 tabular-nums">
        <div className="flex justify-between items-baseline gap-3">
          <span className="font-semibold">{label}</span>
          <span className={cn("text-sm whitespace-nowrap", tone === "full" ? "text-gr-danger" : tone === "near" ? "text-gr-warning" : "text-muted-foreground")}>
            {used}{tone === "full" && " · Budget reached"}
          </span>
        </div>
        {cap != null && <BudgetBar p={p} tone={tone} />}
        <span className="text-sm text-muted-foreground">{sub}</span>
      </div>
      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <label className="flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
          <span className="text-muted-foreground">$</span>
          <input type="number" min="0" step="1" aria-label={aria} value={value} onChange={(e) => setDraft(e.target.value)} placeholder="No limit"
            className="w-[72px] border-0 bg-transparent text-[13px] outline-none tabular-nums placeholder:text-muted-foreground" data-testid={`budget-input-${row.level}-${row.ref_id}`} />
        </label>
        <Button type="submit" variant={dirty ? "default" : "outline"} aria-disabled={!dirty} disabled={saving} data-testid={`budget-save-${row.level}-${row.ref_id}`}>Save</Button>
      </form>
    </div>
  );
}

function Group({ title, sub, children }) {
  return (
    <Card className="px-5 py-[18px] flex flex-col gap-1">
      <div className="flex flex-col gap-0.5 pb-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">{title}</span>
        <span className="text-sm text-muted-foreground">{sub}</span>
      </div>
      {children}
    </Card>
  );
}

export default function Budgets() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["budgets"], queryFn: async () => (await api.get("/budgets")).data });
  const save = useMutation({
    mutationFn: async ({ level, ref_id, amount_usd }) => api.put("/budgets", { level, ref_id, amount_usd }),
    onSuccess: (_, { label, amount_usd, done }) => {
      ["budgets", "overview", "overview-counts", "alerts"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      done();
      toast.success(`${label} set to ${usd(amount_usd)}.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const header = (status) => (
    <div className="flex flex-col gap-1.5">
      <PageHeader eyebrow={`Budgets · ${month()}`} title="A request runs only when every budget it falls under has room." status={status} />
      <span className="text-sm text-muted-foreground">You're alerted at 50%, 80% and 100%. The marks on each bar show where.</span>
    </div>
  );
  if (isLoading || !data) return <Page>{header()}<Loading /></Page>;

  const of = (level) => data.scopes.filter((r) => r.level === level);
  const company = of("company")[0];
  const projects = of("project");
  const monthly = Object.fromEntries(of("coder_monthly").map((r) => [r.ref_id, r]));
  const people = of("coder_daily").map((d) => [d, monthly[d.ref_id]]);
  const objectives = of("objective").filter((r) => r.amount_usd);
  const full = [company, ...projects, ...people.flat()].filter((r) => r && isFull(r)).length;
  const row = (r, label, sub, aria) => (
    <BudgetRow key={`${r.level}:${r.ref_id}`} row={r} label={label} sub={sub} aria={aria} saving={save.isPending}
      onSave={(amount_usd, done) => save.mutate({ level: r.level, ref_id: r.ref_id, amount_usd, label, done })} />
  );
  return (
    <Page data-testid="owner-budgets">
      {header(full === 0 ? "Every budget has room." : `${full} ${full === 1 ? "budget is" : "budgets are"} full.`)}
      <Group title="Company" sub={`Everything ${data.company_name || "your company"} spends this month, on every project.`}>
        {company && row(company, "Monthly budget", `Resets on ${nextMonth()}.`, "Company monthly budget in dollars")}
      </Group>
      <Group title="Projects" sub="Monthly, per repository.">
        {projects.length ? projects.map((r) => row(r, r.name, r.sessions_month ? `${plural(r.sessions_month, "session")} this month.` : "No sessions this month.",
          `${r.name} monthly budget in dollars`))
          : <span className="py-3.5 border-t text-muted-foreground">No projects yet.</span>}
      </Group>
      <Group title="People" sub="A daily and a monthly budget for each person, across all their sessions.">
        {people.length ? people.flatMap(([d, m]) => [
          row(d, `${d.name}, today`, "Resets each day.", `${d.name} daily budget in dollars`),
          m && row(m, `${m.name}, ${month()}`, `Resets on ${nextMonth()}.`, `${m.name} monthly budget in dollars`),
        ]) : <span className="py-3.5 border-t text-muted-foreground">Nobody on the team yet.</span>}
      </Group>
      <Card className="px-5 py-[18px] flex flex-col gap-1">
        <div className="flex flex-col gap-0.5 pb-2">
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Objectives</span>
          <span className="text-sm text-muted-foreground">A total for the life of each objective. Change them on the <Link to="/founder/objectives" className="underline underline-offset-2 hover:opacity-50">Objectives page</Link>.</span>
        </div>
        {objectives.length ? objectives.map((o) => (
          <div key={o.ref_id} className="flex justify-between items-center gap-4 py-2.5 border-t tabular-nums">
            <span className="min-w-0">{o.name}</span>
            <span className={cn("text-sm whitespace-nowrap", o.spent_usd >= o.amount_usd ? "text-gr-danger" : "text-muted-foreground")}>
              {usd(o.spent_usd)} of {usd(o.amount_usd)} · {Math.round((o.spent_usd / o.amount_usd) * 100)}%
            </span>
          </div>
        )) : <span className="py-2.5 border-t text-muted-foreground">No objective has a budget yet.</span>}
      </Card>
      <p className="m-0 text-sm text-muted-foreground">Each session can also have its own cap, set when the session is started. A blocked request shows "Budget reached" in the session.</p>
    </Page>
  );
}
